import type { GridConfig, VehicleConfig, VehicleType } from '@config'
import { COUPLING_GAP, LOCO_DEFAULT_SPEED } from './constants'
import { linkVehicles } from './coupling'
import { cellKey, portKey } from './keys'
import { moveVehicleBy } from './movement'
import { rebuildOccupiedCells } from './queries'
import type { Locomotive, Network, Vehicle, Wagon } from './types'

const LOCO_DEFAULT_LENGTH = 0.8
const LOCO_DEFAULT_ACCELERATION = 1.5
const LOCO_DEFAULT_DECELERATION = 3

const WAGON_DEFAULT_LENGTH = 0.7
const WAGON_DEFAULT_SPACING = WAGON_DEFAULT_LENGTH + COUPLING_GAP
const WAGON_DEFAULT_COLOR = 'coral'
const WAGON_DEFAULT_T = 0

function findSpawnEdge(network: Network, x: number, y: number) {
  const key = cellKey(x, y)
  const prefix = `${key}:`
  return Array.from(network.edges.values()).find(
    edge => edge.type === 'internal' && (edge.a.startsWith(prefix) || edge.b.startsWith(prefix)),
  )?.id
}

function getEdgeMidT(network: Network, edgeId: string): number {
  return (network.edges.get(edgeId)?.length ?? 1) / 2
}

interface SpawnOptions {
  id?: string
  direction?: 1 | -1
  t?: number
  color?: string
}

function createVehicle(
  type: VehicleType,
  network: Network,
  edgeId: string,
  options: SpawnOptions = {},
): Vehicle {
  const isLoco = type === 'locomotive' || type === 'npc'

  const id =
    options.id ??
    (type === 'locomotive'
      ? 'L1'
      : type === 'npc'
        ? `NPC_L${network.npcs.size + 1}`
        : `W${network.wagons.size + 1}`)

  const length = isLoco ? LOCO_DEFAULT_LENGTH : WAGON_DEFAULT_LENGTH
  const t = options.t ?? (type === 'npc' ? getEdgeMidT(network, edgeId) : WAGON_DEFAULT_T)

  const baseVehicle = {
    id,
    type,
    edgeId,
    direction: options.direction ?? 1,
    length,
    t,
  }

  if (isLoco) {
    const loco: Locomotive = {
      ...baseVehicle,
      type: type as 'locomotive' | 'npc',
      speed: LOCO_DEFAULT_SPEED,
      currentVelocity: type === 'npc' ? LOCO_DEFAULT_SPEED : 0,
      acceleration: LOCO_DEFAULT_ACCELERATION,
      deceleration: LOCO_DEFAULT_DECELERATION,
    }

    if (type === 'locomotive') network.locomotive = loco
    else network.npcs.set(loco.id, loco)

    return loco
  } else {
    const wagon: Wagon = {
      ...baseVehicle,
      type: 'wagon',
      color: options.color ?? WAGON_DEFAULT_COLOR,
    }

    network.wagons.set(wagon.id, wagon)
    return wagon
  }
}

function spawnTrain(
  type: 'locomotive' | 'npc',
  network: Network,
  edgeId: string,
  options: SpawnOptions & { wagons?: number } = {},
) {
  let prevVehicle = createVehicle(type, network, edgeId, options)

  const wagonCount = options.wagons ?? 0
  const spacing = WAGON_DEFAULT_SPACING

  for (let i = 0; i < wagonCount; i++) {
    // Sposta una "sonda" all'indietro per trovare la posizione esatta sul binario
    const probe: Vehicle = { ...prevVehicle, id: '__probe__' }
    moveVehicleBy(network, probe, -spacing)

    const wagon = createVehicle('wagon', network, probe.edgeId, {
      id: `NPC_W${network.wagons.size + 1}`,
      direction: probe.direction,
      t: probe.t,
      color: 'gray',
    })

    linkVehicles(network, prevVehicle, 'linkedBack', wagon, 'linkedFront')
    prevVehicle = wagon
  }
}

export function spawnEntitiesFromGrid(gridInput: GridConfig, network: Network) {
  for (let y = 0; y < gridInput.length; y++) {
    for (let x = 0; x < gridInput[y].length; x++) {
      const raw = gridInput[y][x]
      if (!raw || typeof raw !== 'object' || Array.isArray(raw) || !('vehicle' in raw)) continue

      let edgeId: string | undefined
      let entranceDirection: 1 | -1 | undefined

      if ('entrance' in raw && raw.entrance) {
        const candidateEdgeId = `ENTRANCE:${portKey(x, y, raw.entrance)}`
        if (network.edges.has(candidateEdgeId)) {
          edgeId = candidateEdgeId
          entranceDirection = 1
        }
      }

      edgeId ??= findSpawnEdge(network, x, y)
      if (!edgeId) continue

      const vehicle =
        typeof raw.vehicle === 'string'
          ? { type: raw.vehicle as VehicleType }
          : (raw.vehicle as VehicleConfig)

      const direction = entranceDirection ?? ((vehicle.direction ?? 1) as 1 | -1)

      if (vehicle.type === 'locomotive' || vehicle.type === 'npc') {
        const wagonCount = vehicle.wagons ?? 0
        const spacing = WAGON_DEFAULT_SPACING
        const t =
          entranceDirection != null ? wagonCount * spacing + LOCO_DEFAULT_LENGTH : WAGON_DEFAULT_T

        spawnTrain(vehicle.type, network, edgeId, {
          direction,
          t,
          wagons: wagonCount,
        })
      } else if (vehicle.type === 'wagon') {
        createVehicle('wagon', network, edgeId, {
          direction,
          color: vehicle.color,
        })
      }
    }
  }
  rebuildOccupiedCells(network)
}
